// @flow
import React from 'react'
import OlVectorSource from 'ol/source/Vector'
import OlVectorLayer from 'ol/layer/Vector'
import WKT from 'ol/format/WKT'
import * as OlObservable from 'ol/Observable'
import OlInteractionDragBox from 'ol/interaction/DragBox'
import OlInteractionSelect from 'ol/interaction/Select'
import { always as OlConditionAlways} from 'ol/events/condition'
import OlStyle from 'ol/style/Style'
import OlStyleStroke from 'ol/style/Stroke'
import * as OlExtent from 'ol/extent'
import * as olUtils from 'catena/er2_ui/util/ol'

const styles = {
    attribute: {
        color: 'darkblue',
    },
    attributesContainer: {
        margin: '6px 2px',
    },
    closeButton: {
        backgroundColor: 'white',
        border: '1px solid gray',
        borderRadius: '2px',
        height: '14px',
        marginTop: '1px',
        marginRight: '1px',
        padding: '0px 1px',
        position: 'absolute',
        top: '3px',
        right: '3px',
        zIndex: 2,
    },
    closeButtonIcon: {
        color: 'black',
    },
    container: {
        backgroundColor: 'lightgray',
        fontSize: 'small',
    },
    main: {
        padding: '4px',
    },
    select: {
        fontSize: 'small',
    },
    table: {
        display: 'block',
        width: '100%',
    },
    tbody: {
        display: 'inline-block',
        height: '200px',
        overflow: 'auto',
        width: '100%',
    },
    titleBar: {
        backgroundColor: 'rgba(11, 78, 41, .7)',
        color: 'white',
        padding: '4px',
    },
}

class Identify {
    constructor(map, onIdentify) {
        this.map = map
        // Current identify overlay
        this.overlayId = undefined

        this.interaction = new OlInteractionDragBox({
            condition: OlConditionAlways,
            style: new OlStyle({
                stroke: new OlStyleStroke({
                    color: [0, 0, 255, 1],
                }),
            }),
        })
        this.onIdentify = onIdentify
    }

    getStyle(feature, resolution) {
        const style = olUtils.getOlStyles()
        return style
    }

    identify() {
        const mapcontainer = document.getElementById('map')
        let singleClickKey
        if (this.interaction && singleClickKey) {
            this.map.removeInteraction(this.interaction)
            OlObservable.unByKey(singleClickKey)
        }

        const doIdentify = (extent) => {
            OlObservable.unByKey(singleClickKey)
            this.map.removeInteraction(this.interaction)
            if (this.vectorLayer) {
                this.vectorLayer.getSource().clear()
            }
            mapcontainer.style.cursor = 'inherit'
            this.onIdentify(extent)
        }

        // Separate events for single click and box selection.
        singleClickKey = this.map.olmap.on('singleclick', (evt) => {
            const coords = evt.coordinate
            let extent = [
                coords[0], coords[1], coords[0], coords[1],
            ]
            // Buffer units are meters
            const res = this.map.getResolution()
            extent = OlExtent.buffer(extent, res * 10)
            doIdentify(extent)
        })

        this.interaction.on('boxend', () => {
            const extent = this.interaction.getGeometry().getExtent()
            doIdentify(extent)
        })

        this.map.addInteraction(this.interaction)
        mapcontainer.style.cursor = 'pointer'
    }

    selectFeat = (feat, coord) => {
        // Remove existing overlays
        if (this.overlayId) {
            this.map.removeOverlay(this.overlayId)
        }

        const getNameField = (f) => {
            const keys = f.getKeys()
            const names = keys.filter(k => k !== 'geometry' && k[0] !== '_')
            return names[0]
        }

        const closeOverlay = () => {
            this.map.removeOverlay(this.overlayId)
        }

        const setFeat = (ev) => {
            const featId = ev.target.value
            const selFeat = this.vectorLayer.getSource().getFeatureById(featId)
            const geom = selFeat.getGeometry()
            let clickPoint = geom
            if (geom.getPoint) clickPoint = geom.getPoint()
            else if (geom.getInteriorPoint) clickPoint = geom.getInteriorPoint()
            else if (geom.getInteriorPoints) clickPoint = geom.getInteriorPoints()
            // Interior points can generate a third coordinate
            const clickCoord = clickPoint.getFirstCoordinate()
            this.selectFeat(selFeat, clickCoord)
        }

        if (feat) {
            const allfeats = this.vectorLayer.getSource().getFeatures()
            // Set current feature
            const nameField = getNameField(feat)
            this.overlayId = this.map.addPlacemark(coord, (
                <div style={styles.container}>
                    <button style={styles.closeButton} onClick={closeOverlay}>
                        <i
                            className={'fa fa-close close'}
                            style={styles.closeButtonIcon}
                        />
                    </button>
                    <div style={styles.titleBar}>
                        {feat.get('_layer')}
                    </div>
                    <div style={styles.main}>
                        <select
                            onChange={setFeat}
                            style={styles.select}
                            value={feat.getId()}
                        >
                            {allfeats.map(f => (
                                <option
                                    key={f.getId()}
                                    value={f.getId()}
                                >
                                    {`${f.get('_layer')}: ${f.get(nameField)}`}
                                </option>
                            ))}
                        </select>
                        <div style={styles.attributesContainer}>
                            <table style={styles.table}>
                                <tbody style={styles.tbody}>
                                    {feat.getKeys().filter(k => k !== 'geometry' && k[0] !== '_').map(k => (
                                        <tr key={k}>
                                            <td style={styles.attribute}>{k}:</td>
                                            <td>{feat.get(k)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <span className={'pulse'} />
                </div>
            ), {
                close: closeOverlay,
            })
        }
    }

    /**
     *
     * @param props maptoolbar props
     */
    processIdentify(props) {
        if (!props.identify.identifyHandled) {
            props.onIdentifyHandled()
            // Clear identify vector layer and add new features.
            if (!this.vectorLayer) {
                const vectorSource = new OlVectorSource({
                    projection: 'EPSG:4326',
                })
                this.vectorLayer = new OlVectorLayer({
                    source: vectorSource,
                    style: (feat, res) => this.getStyle(feat, res),
                    zIndex: 1,
                })

                // Add single click selection of features
                this.singleClickSelect = new OlInteractionSelect({
                    layers: [this.vectorLayer],
                    multi: true,
                })
                this.singleClickSelect.on('select', (e) => {
                    const feats = e.selected
                    const feat = feats[0]
                    this.selectFeat(feat, e.mapBrowserEvent.coordinate)
                })

                this.map.olmap.addLayer(this.vectorLayer)
                this.map.olmap.addInteraction(this.singleClickSelect)
            }

            const wkt = new WKT({ dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
            let cnt = 0
            props.identify.layers.forEach(layer => layer.features.forEach((f, i) => {
                const feat = wkt.readFeature(f.geometry)
                feat.set('_layer', layer.layer_name)
                feat.setId(++cnt)
                Object.entries(f).forEach(([k, v]) => {
                    if (k !== 'geometry') feat.set(k, v)
                })
                this.vectorLayer.getSource().addFeature(feat)
                this.selectFeat(feat, OlExtent.getCenter(feat.getGeometry().getExtent()))
            }))
        }
    }
}

export default Identify
